import { Box } from '@mui/system';
import { FC, Fragment, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';

import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { sortWithPriority } from 'src/utils/sort-labels';

import { AuthContext } from '../../providers/auth-context';

import { countries, defaultOption, residency, userDataKeys } from './constants';
import { styles } from './styles';
import { SpanishTaxDetailsFormProps, UserDataFormValues } from './types';
import { hasSpanishDetails, useUtils } from './Utils';

export const SpanishTaxDetails: FC<SpanishTaxDetailsFormProps<UserDataFormValues>> = ({
    control,
    userHasBookings,
}) => {
    const userState = useContext(AuthContext);
    const { t } = useTranslation();
    const { hasNationality, spanishResidentRequired, nifRequired, nieRequired, passportRequired } =
        useUtils();

    const residencyOptions = [defaultOption, ...residency];

    const nifNumber = useWatch({ control, name: 'nifNumber' });
    const nieNumber = useWatch({ control, name: 'nieNumber' });
    const passportNumber = useWatch({ control, name: 'passportNumber' });
    const passportIssuingCountry = useWatch({ control, name: 'passportIssuingCountry' });

    const sortedIssuingCountries = useMemo(
        () => [
            defaultOption,
            ...sortWithPriority(t, countries, ['countries_united_kingdom'], ['countries_other']),
        ],
        [t]
    );
    const userSpanishTaxInfo = hasSpanishDetails(userState);

    const isOccupantsPage = window.location.href.includes('occupants');

    const userHasSpanishResidenceInfo =
        userSpanishTaxInfo ||
        (isOccupantsPage &&
            (!!nifNumber || !!nieNumber || (!!passportNumber && !!passportIssuingCountry)));

    return hasNationality ? (
        <Box css={styles.spanishTaxDetailsContainer}>
            {!nifRequired ? (
                <Box css={styles.inputGroupWrapper}>
                    <ControlledField
                        fieldType={FieldTypes.Select}
                        name={userDataKeys.isSpanishResident}
                        control={control}
                        label={t('your_details_spanish_resident')}
                        placeholder={t('your_details_spanish_resident')}
                        fullWidth
                        options={residencyOptions}
                        required={spanishResidentRequired}
                        id="user-details-is-spanish-confirmation"
                    />
                </Box>
            ) : null}
            {nifRequired ? (
                <Box css={styles.inputGroupWrapper}>
                    <ControlledField
                        name={userDataKeys.nifNumber}
                        control={control}
                        label={t('your_details_nif')}
                        placeholder={t('your_details_nif')}
                        fullWidth
                        required
                        disabled={userHasBookings && userHasSpanishResidenceInfo}
                        id="user-details-nif"
                    />
                </Box>
            ) : null}
            {nieRequired ? (
                <Box css={styles.inputGroupWrapper}>
                    <ControlledField
                        name={userDataKeys.nieNumber}
                        control={control}
                        label={t('your_details_nie')}
                        placeholder={t('your_details_nie')}
                        fullWidth
                        required={nieRequired}
                        disabled={userHasBookings && userHasSpanishResidenceInfo}
                        id="user-details-nie"
                    />
                </Box>
            ) : null}

            {passportRequired ? (
                <Fragment>
                    <Box css={styles.inputGroupWrapper}>
                        <ControlledField
                            name={userDataKeys.passportNumber}
                            control={control}
                            label={t('your_details_passport')}
                            placeholder={t('your_details_passport')}
                            fullWidth
                            required={passportRequired}
                            disabled={userHasBookings && userHasSpanishResidenceInfo}
                            id="user-details-passport"
                        />
                    </Box>
                    <Box css={styles.inputGroupWrapper}>
                        <ControlledField
                            fieldType={FieldTypes.Autocomplete}
                            name={userDataKeys.passportIssuingCountry}
                            control={control}
                            label={t('your_details_passport_country')}
                            fullWidth
                            required
                            options={sortedIssuingCountries}
                            disabled={userHasBookings && userHasSpanishResidenceInfo}
                            id="user-details-passport-country"
                        />
                    </Box>
                </Fragment>
            ) : null}
        </Box>
    ) : null;
};
